import React, { useState } from 'react';
import Layout from '../components/Layout';

const leaders = [
  {
    name: 'Mr. Bhupendra Kumar (Ishaan)',
    title: 'Founder & Managing Director',
    company: 'Sharda Group',
    initials: '/Ishaan.jpg',
    color: '#3F72AF',
    bio: [
      'Mr. Bhupendra Kumar is the visionary architect behind Sharda Group — a travel enterprise he founded on the conviction that the industry deserved something far better than mediocrity. His career has been defined not by incremental progress, but by bold leaps: identifying gaps in the travel services sector before others saw them, and building solutions that set entirely new benchmarks for quality and client experience.',
      'Under his stewardship, Sharda Group has grown from a single-minded idea into a multi-city operation spanning West Africa and South Asia. Mr. Kumar\'s leadership philosophy is rooted in a simple but demanding principle — that every traveller, regardless of budget or destination, deserves service that feels genuinely personal. That philosophy has become the cultural DNA of every office, every team, and every client relationship.',
      'His ambition does not rest at current borders. Mr. Ishaan Kumar\'s vision is explicitly global: to build a travel company that serves the world with the same warmth and precision it brought to its very first client.'
    ]
  },
  {
    name: 'Ms. Meenakshi Ajaniya',
    title: 'Chief Financial Officer',
    company: 'Sharda Group',
    initials: '/meenakshi.jpeg',
    color: '#2d6a9f',
    bio: [
      'Ms. Meenakshi Ajaniya brings to Sharda Group a rare and powerful combination of financial discipline and long-range strategic foresight. As Chief Financial Officer, she oversees the complete financial architecture of the organisation — from treasury operations and risk management to regulatory compliance and capital planning — ensuring that the company\'s growth is not merely ambitious, but structurally and ethically sound.',
      'Her approach to financial leadership moves well beyond conventional stewardship. Ms. Ajaniya has been instrumental in designing the financial systems that allow Sharda Group to operate with confidence across multiple currencies, regulatory environments, and international markets. Her rigorous standards for accuracy, transparency, and ethical conduct have earned the enduring trust of banking partners, institutional clients, and regulatory bodies alike.',
      'Ms. Ajaniya holds the view that financial strength is not an end in itself, but the foundation that allows a company to serve its clients without compromise — and every decision she makes is oriented toward exactly that purpose.'
    ]
  },
  {
    name: 'CA Karan Ajaniya',
    title: 'Head of Global Operations',
    company: 'Sharda Group',
    initials: 'karan.jpeg',
    color: '#1a5c91',
    bio: [
      'CA Karan Ajaniya leads Sharda Group\'s India operations from Jaipur with a precision and clarity that reflects his distinguished background as a Chartered Accountant. His rare ability to synthesise financial rigour with operational leadership has made him an indispensable force in the organisation\'s growth across the subcontinent.',
      'Mr. Ajaniya has been the principal architect of Sharda Group\'s India-facing business — building client relationships, establishing operational processes, and positioning the Jaipur office as a centre of excellence for the company\'s South Asian operations. His work ethic, attention to detail, and commitment to service quality set the standard for everyone around him.',
      'Beyond the numbers and operations, CA Karan Ajaniya is regarded within the organisation as a steady and trusted voice — someone whose counsel is sought precisely because it is always grounded in both analytical rigour and genuine care for the company\'s long-term direction.'
    ]
  }
];

function LeaderCard({ leader, index }) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered]   = useState(false);

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: hovered
          ? '0 24px 64px rgba(17,45,78,0.16)'
          : '0 6px 28px rgba(17,45,78,0.08)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'transform 0.35s ease, box-shadow 0.35s ease'
      }}
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
    >
      {/* Top accent bar */}
      <div style={{ height: 5, background: 'linear-gradient(90deg, ' + leader.color + ', #DBE2EF)' }} />

      <div style={{ padding: '32px 32px 28px' }}>
        {/* Avatar + name row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, ' + leader.color + ', ' + leader.color + '88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem', fontWeight: 700, color: '#fff',
            boxShadow: '0 8px 24px ' + leader.color + '44'
          }}>
            <img src={leader.initials} style={{borderRadius: '50%'}}/>
          </div>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', color: 'var(--bj-espresso)' }}>
              {leader.name}
            </h3>
            <p style={{
              margin: '0 0 3px', fontSize: '0.85rem',
              fontWeight: 700, color: leader.color
            }}>
              {leader.title}
            </p>
            <p style={{ margin: 0, fontSize: '0.78rem', opacity: 0.55 }}>
              {leader.company}
            </p>
          </div>
        </div>

        {/* First paragraph always visible */}
        <p style={{
          margin: '0 0 16px', fontSize: '0.92rem',
          lineHeight: 1.78, color: 'var(--bj-espresso)', opacity: 0.8
        }}>
          {leader.bio[0]}
        </p>

        {/* Expandable bio */}
        {expanded && (
          <div style={{
            overflow: 'hidden',
            animation: 'fadeIn 0.4s ease'
          }}>
            {leader.bio.slice(1).map(function(para, i) {
              return (
                <p key={i} style={{
                  margin: '0 0 16px', fontSize: '0.92rem',
                  lineHeight: 1.78, color: 'var(--bj-espresso)', opacity: 0.8
                }}>
                  {para}
                </p>
              );
            })}
          </div>
        )}

        {/* Read more / less toggle */}
        <button
          onClick={function() { setExpanded(!expanded); }}
          style={{
            background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', fontFamily: 'var(--font-body)',
            fontWeight: 700, fontSize: '0.85rem',
            color: leader.color,
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 4
          }}
        >
          {expanded ? 'Read less' : 'Read full profile'}
          <span style={{
            display: 'inline-block',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease'
          }}>→</span>
        </button>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

export default function Leadership() {
  return (
    <Layout>
      {/* HERO BAND */}
      <div style={{
        background: 'linear-gradient(135deg, var(--bj-espresso) 0%, #1e3f6b 100%)',
        padding: '80px 0 72px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -60, right: -60, width: 300, height: 300, borderRadius: '50%', background: 'rgba(63,114,175,0.12)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(63,114,175,0.08)', pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative' }}>
          <span style={{
            display: 'inline-block',
            fontSize: '0.72rem', fontWeight: 700,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--bj-sand)', marginBottom: 14,
            fontFamily: 'var(--font-body)'
          }}>
            The people behind the journey
          </span>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            color: '#fff', margin: '0 0 18px', fontWeight: 600
          }}>
            Our Leadership
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.65)',
            fontSize: '1.05rem', lineHeight: 1.7,
            maxWidth: 560, margin: 0
          }}>
            Bluejay Travels is built on the foundation of experienced, principled leadership. Meet the people whose vision, discipline, and care shape every journey we plan.
          </p>
        </div>
      </div>

      {/* FLIGHT PATH DIVIDER */}
      <div style={{ background: 'var(--bj-cream-soft)', padding: '0' }}>
        <div className="flight-path" style={{ maxWidth: 1180, margin: '0 auto' }} />
      </div>

      {/* LEADER CARDS */}
      <section className="section" style={{ background: 'var(--bj-cream-soft)' }}>
        <div className="container">
        <div className="grid-3">
            {leaders.map(function(leader, i) {
              return <LeaderCard key={leader.name} leader={leader} index={i} />;
            })}
          </div>

          {/* Bottom quote */}
          <div style={{
            marginTop: 64,
            padding: '36px 40px',
            background: 'var(--bj-espresso)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 32
          }}>
            <span style={{ fontSize: '2.5rem', flexShrink: 0 }}>🪶</span>
            <div>
              <p style={{
                margin: '0 0 8px',
                fontFamily: 'var(--font-display)',
                fontSize: '1.25rem',
                color: '#fff',
                fontStyle: 'italic',
                lineHeight: 1.5
              }}>
                "We don't just move people from place to place. We carry their dreams, their plans, and the moments they will remember for the rest of their lives."
              </p>
              <p style={{ margin: 0, color: 'var(--bj-sand)', fontSize: '0.85rem', fontWeight: 700 }}>
                — Mr. Bhupendra Kumar, Founder
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}